"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowDown, ArrowUp } from "lucide-react";
import { MedicineLabel } from "@/entities/medicine/ui/medicine-label";
import { toPromoDetails, type OrganizationMetric, type OrganizationMetricMedicine, type OrganizationMetricSortBy, type OrganizationMetricSortDir } from "@/entities/organization/api/organization-metric-drilldown";
import { getShelfLifeStatus } from "@/shared/lib/expiry-status";
import { formatExpiryDate, formatIssueDate } from "@/shared/lib/format/dates";
import { fmtUzs, formatCount } from "@/shared/lib/format/numbers";
import { cn } from "@/shared/lib/utils";
import type { ListTableColumn } from "@/shared/ui/list-table";
import { PriceDeltaPill } from "@/shared/ui/price-delta-pill";
import { PromoBadge } from "@/shared/ui/promo-badge";
import { ShelfLifeCell } from "@/shared/ui/shelf-life-cell";
type Translate = (key: string, options?: Record<string, unknown>) => string;
const SORTABLE_FIELDS: Record<OrganizationMetric, OrganizationMetricSortBy[]> = {
  exclusive: ["medicine_name", "matched_days", "last_matched_date"],
  cheapest: ["medicine_name", "matched_days", "matched_price", "price_gap_pct"],
  promotion: ["medicine_name", "matched_offers"]
};

// Widths include cell padding (border-box). The medicine column has no width so it absorbs the remaining space.
const COLUMN_WIDTH = {
  price: "w-28",
  runnerUp: "w-52",
  delta: "w-20",
  days: "w-32",
  promo: "w-44",
  issued: "w-28",
  expiry: "w-32"
} as const;
function medicineCell(row: OrganizationMetricMedicine) {
  return <MedicineLabel medicine={row.medicine ?? {}} nameOverride={row.medicine ? null : row.raw_name} />;
}
function DrilldownPriceCell(t0) {
  const $ = _c(15);
  const {
    row
  } = t0;
  const regular = row.promotion?.regular_price ?? row.latest?.price ?? row.matched_price;
  const promo = row.promotion?.promo_price;
  if (promo != null && regular != null && promo !== regular) {
    let t1;
    if ($[0] !== regular) {
      t1 = fmtUzs(regular);
      $[0] = regular;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <span className="text-muted-foreground text-[10px] line-through">{t1}</span>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== promo) {
      t3 = fmtUzs(promo);
      $[4] = promo;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== t3) {
      t4 = <span className="font-medium">{t3}</span>;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    let t5;
    if ($[8] !== t2 || $[9] !== t4) {
      t5 = <span className="flex flex-col tabular-nums">{t2}{t4}</span>;
      $[8] = t2;
      $[9] = t4;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  }
  const t1 = promo ?? regular;
  let t2;
  if ($[11] !== t1) {
    t2 = fmtUzs(t1);
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== t2) {
    t3 = <span className="tabular-nums">{t2}</span>;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
}
function expiryCell(row: OrganizationMetricMedicine, intlLocale: string) {
  const expiry = row.latest?.expiry_date;
  return <ShelfLifeCell text={formatExpiryDate(expiry, intlLocale)} status={getShelfLifeStatus(expiry)} />;
}
function gapCell(row: OrganizationMetricMedicine) {
  const matched = row.matched_price;
  const rival = row.rival_price;
  if (matched == null || rival == null || rival === 0) return "—";
  return <PriceDeltaPill tone={{
    name: matched < rival ? "below" : "above",
    percent_delta: (matched - rival) / rival * 100
  }} />;
}
export function buildDrilldownColumns({
  metric,
  intlLocale,
  isMobile,
  sortBy,
  sortDir,
  onSort,
  t
}: {
  metric: OrganizationMetric;
  intlLocale: string;
  isMobile: boolean;
  sortBy: OrganizationMetricSortBy;
  sortDir: OrganizationMetricSortDir;
  onSort: (field: OrganizationMetricSortBy) => void;
  t: Translate;
}): ListTableColumn<OrganizationMetricMedicine>[] {
  const sortableHeader = (label: string, field: OrganizationMetricSortBy, align: "left" | "right" = "left"): Pick<ListTableColumn<OrganizationMetricMedicine>, "header" | "ariaSort"> => SORTABLE_FIELDS[metric].includes(field) ? {
    header: <button type="button" onClick={() => onSort(field)} className={cn("inline-flex w-full min-w-0 items-center gap-1", align === "right" && "justify-end")}>
              {label}
              {sortBy === field ? sortDir === "asc" ? <ArrowUp className="size-3" aria-hidden /> : <ArrowDown className="size-3" aria-hidden /> : null}
            </button>,
    ariaSort: sortBy === field ? sortDir === "asc" ? "ascending" : "descending" : "none"
  } : {
    header: label
  };
  const columns = {
    medicine: {
      id: "medicine",
      ...sortableHeader(t("organizations.ratings.drilldown.columns.medicine"), "medicine_name"),
      cell: medicineCell
    },
    days: {
      id: "days",
      ...sortableHeader(t("organizations.ratings.drilldown.columns.daysOffered"), "matched_days", "right"),
      cell: (row: OrganizationMetricMedicine) => formatCount(row.matched_days),
      align: "right" as const,
      headerClassName: COLUMN_WIDTH.days,
      cellClassName: COLUMN_WIDTH.days
    },
    offers: {
      id: "offers",
      ...sortableHeader(t("organizations.ratings.drilldown.columns.offers"), "matched_offers", "right"),
      cell: (row: OrganizationMetricMedicine) => formatCount(row.matched_offers),
      align: "right" as const,
      headerClassName: COLUMN_WIDTH.days,
      cellClassName: COLUMN_WIDTH.days
    },
    price: {
      id: "price",
      ...sortableHeader(t("organizations.ratings.drilldown.columns.price"), "matched_price", "right"),
      cell: (row: OrganizationMetricMedicine) => <DrilldownPriceCell row={row} />,
      align: "right" as const,
      headerClassName: COLUMN_WIDTH.price,
      cellClassName: COLUMN_WIDTH.price
    },
    runnerUp: {
      id: "runner-up",
      header: t("organizations.ratings.drilldown.columns.runnerUp"),
      cell: (row: OrganizationMetricMedicine) => <span className="flex flex-col tabular-nums">
          <span>{fmtUzs(row.rival_price)}</span>
          {row.competitor_count != null ? <span className="text-muted-foreground text-[10px]">
              {t("organizations.ratings.drilldown.competitors", {
            count: row.competitor_count
          })}
            </span> : null}
        </span>,
      align: "right" as const,
      headerClassName: COLUMN_WIDTH.runnerUp,
      cellClassName: COLUMN_WIDTH.runnerUp
    },
    delta: {
      id: "delta",
      ...sortableHeader(t("organizations.ratings.drilldown.columns.delta"), "price_gap_pct", "right"),
      cell: gapCell,
      align: "right" as const,
      headerClassName: COLUMN_WIDTH.delta,
      cellClassName: COLUMN_WIDTH.delta
    },
    promo: {
      id: "promo",
      header: t("organizations.ratings.drilldown.columns.promo"),
      cell: (row: OrganizationMetricMedicine) => <div className="flex min-w-0 items-center gap-2">
          <PromoBadge promo={toPromoDetails(row)} intlLocale={intlLocale} />
          <span className="text-muted-foreground truncate text-xs">{row.promotion?.promotion_text ?? "—"}</span>
        </div>,
      headerClassName: COLUMN_WIDTH.promo,
      cellClassName: COLUMN_WIDTH.promo
    },
    issued: {
      id: "issued",
      ...sortableHeader(t("organizations.ratings.drilldown.columns.issued"), "last_matched_date"),
      cell: (row: OrganizationMetricMedicine) => formatIssueDate(row.last_matched_date, intlLocale),
      headerClassName: COLUMN_WIDTH.issued,
      cellClassName: COLUMN_WIDTH.issued
    },
    expiry: {
      id: "expiry",
      header: t("organizations.ratings.drilldown.columns.expiry"),
      cell: (row: OrganizationMetricMedicine) => expiryCell(row, intlLocale),
      headerClassName: COLUMN_WIDTH.expiry,
      cellClassName: COLUMN_WIDTH.expiry
    }
  };
  if (metric === "exclusive") {
    return isMobile ? [columns.medicine, columns.days, columns.price] : [columns.medicine, columns.days, columns.price, columns.issued, columns.expiry];
  }
  if (metric === "cheapest") {
    return isMobile ? [columns.medicine, columns.price, columns.delta] : [columns.medicine, columns.price, columns.runnerUp, columns.delta, columns.days, columns.expiry];
  }
  return isMobile ? [columns.medicine, columns.promo, columns.price] : [columns.medicine, columns.promo, columns.price, columns.offers, columns.expiry];
}